(() => {
  const DEBOUNCE_MS = 180;
  const MIN_CHARS = 2;
  const LIMIT = 6;
  const aliases = {
    'bun': 'bún', 'bun bo': 'bún bò', 'bun hue': 'bún huế',
    'mam': 'mắm', 'mam ruoc': 'mắm ruốc', 'mam tom': 'mắm tôm',
    'nuoc mam': 'nước mắm', 'cha': 'chả', 'cha hue': 'chả huế',
    'banh': 'bánh', 'banh beo': 'bánh bèo', 'banh loc': 'bánh lọc',
    'do kho': 'đồ khô', 'gia vi': 'gia vị', 'tra': 'trà', 'ca kho': 'cá khô'
  };

  const escapeHTML = (value = '') => value.replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const stripDiacritics = (value = '') => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D');

  const highlight = (title, query) => {
    const plainTitle = stripDiacritics(title).toLowerCase();
    const plainQuery = stripDiacritics(query).toLowerCase().trim();
    if (!plainQuery) return escapeHTML(title);
    const start = plainTitle.indexOf(plainQuery);
    if (start < 0) return escapeHTML(title);
    const before = escapeHTML(title.slice(0, start));
    const match = escapeHTML(title.slice(start, start + plainQuery.length));
    const after = escapeHTML(title.slice(start + plainQuery.length));
    return `${before}<mark class="cdb-smart-search__match">${match}</mark>${after}`;
  };

  const formatMoney = (value, currency) => {
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return '';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(number);
    } catch (_) {
      return `$${number.toFixed(2)}`;
    }
  };

  const buildEndpoint = (query, limit) => {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(limit),
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor'
    });
    return `${window.Shopify?.routes?.root || '/'}search/suggest.json?${params.toString()}`;
  };

  const fetchProducts = async (query, signal) => {
    const response = await fetch(buildEndpoint(query, LIMIT), { signal, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
    const payload = await response.json();
    return payload?.resources?.results?.products || [];
  };

  const initialize = (root) => {
    if (!root || root.dataset.cdbSmartSearchReady === 'true') return;
    root.dataset.cdbSmartSearchReady = 'true';

    const form = root.querySelector('[data-cdb-smart-search-form]');
    const input = root.querySelector('[data-cdb-smart-search-input]');
    const panel = root.querySelector('[data-cdb-smart-search-panel]');
    const list = root.querySelector('[data-cdb-smart-search-results]');
    const status = root.querySelector('[data-cdb-smart-search-status]');
    const footer = root.querySelector('[data-cdb-smart-search-footer]');
    const clear = root.querySelector('[data-cdb-smart-search-clear]');
    if (!form || !input || !panel || !list || !status || !footer) return;

    const currency = root.dataset.currency || 'USD';
    let timer = 0;
    let controller = null;
    let activeIndex = -1;
    let resultLinks = [];

    const close = () => {
      root.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
      resultLinks = [];
    };

    const open = () => {
      root.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
    };

    const setState = (message, showPanel = true) => {
      list.innerHTML = '';
      status.hidden = false;
      status.textContent = message;
      footer.hidden = true;
      if (showPanel) open(); else close();
    };

    const render = (products, query) => {
      if (!products.length) {
        setState(`Không tìm thấy sản phẩm phù hợp với “${query}”.`);
        footer.hidden = false;
        footer.href = `${form.action}?q=${encodeURIComponent(query)}&type=product`;
        footer.querySelector('[data-cdb-smart-search-footer-text]').textContent = 'Tìm rộng hơn';
        return;
      }

      status.hidden = true;
      list.innerHTML = products.map((product, index) => {
        const image = product.featured_image?.url || product.image || '';
        const price = formatMoney(product.price, currency);
        const availableText = product.available === false ? 'Hết hàng' : 'Có thể đặt hàng';
        return `
          <li class="cdb-smart-search__item" role="option" id="cdb-search-option-${index}">
            <a class="cdb-smart-search__link" href="${escapeHTML(product.url)}" data-cdb-search-result aria-selected="false">
              <span class="cdb-smart-search__image">${image ? `<img src="${escapeHTML(image)}&width=136" alt="" loading="lazy" width="68" height="68">` : ''}</span>
              <span>
                <span class="cdb-smart-search__title">${highlight(product.title || '', query)}</span>
                <span class="cdb-smart-search__meta">
                  ${price ? `<span class="cdb-smart-search__price">${escapeHTML(price)}</span>` : ''}
                  <span class="cdb-smart-search__availability">${availableText}</span>
                </span>
              </span>
              <span class="cdb-smart-search__arrow" aria-hidden="true">›</span>
            </a>
          </li>`;
      }).join('');

      footer.hidden = false;
      footer.href = `${form.action}?q=${encodeURIComponent(query)}&type=product`;
      footer.querySelector('[data-cdb-smart-search-footer-text]').textContent = `Xem tất cả kết quả cho “${query}”`;
      resultLinks = Array.from(list.querySelectorAll('[data-cdb-search-result]'));
      activeIndex = -1;
      open();
    };

    const runSearch = async () => {
      const originalQuery = input.value.trim();
      root.classList.toggle('is-typing', originalQuery.length > 0);
      if (originalQuery.length < MIN_CHARS) {
        if (originalQuery.length === 1) setState('Nhập thêm 1 ký tự để tìm sản phẩm.'); else close();
        return;
      }

      controller?.abort();
      controller = new AbortController();
      setState('Đang tìm sản phẩm…');

      const normalized = stripDiacritics(originalQuery).toLowerCase().replace(/\s+/g, ' ').trim();
      const preferredQuery = aliases[normalized] || originalQuery;
      try {
        let products = await fetchProducts(preferredQuery, controller.signal);
        if (!products.length && preferredQuery !== originalQuery) {
          products = await fetchProducts(originalQuery, controller.signal);
        }
        render(products, originalQuery);
      } catch (error) {
        if (error.name === 'AbortError') return;
        setState('Không thể tải kết quả lúc này. Nhấn Enter để tìm kiếm.');
      }
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(runSearch, DEBOUNCE_MS);
    };

    input.addEventListener('input', schedule);
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_CHARS) schedule();
    });

    clear?.addEventListener('click', () => {
      input.value = '';
      root.classList.remove('is-typing');
      close();
      input.focus();
    });

    input.addEventListener('keydown', (event) => {
      if (!root.classList.contains('is-open') || !resultLinks.length) {
        if (event.key === 'Escape') close();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        resultLinks.forEach((link) => link.setAttribute('aria-selected', 'false'));
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        activeIndex = (activeIndex + delta + resultLinks.length) % resultLinks.length;
        const active = resultLinks[activeIndex];
        active.setAttribute('aria-selected', 'true');
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        resultLinks[activeIndex].click();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) close();
    });
  };

  const initAll = () => document.querySelectorAll('[data-cdb-smart-search]').forEach(initialize);
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState !== 'loading') initAll();
})();
